import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  Check,
  Dices,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import { useHaptics } from '@/hooks/useHaptics';
import {
  useDeleteVideoClipMutation,
  useSetActiveVideoClipMutation,
  useUpdateVideoClipMutation,
  type AvatarItem,
  type AvatarVideoClip,
  type Video2dProviderId,
  type VideoClipTransparency,
} from '@/services/avatarApi';

interface Props {
  avatar: AvatarItem;
  /** Open Animate2dModal to create a new clip. */
  onAdd: () => void;
  /** Re-roll the clip's prompt with the same provider, new random seed. */
  onReroll?: (clipId: string) => void;
  /** Replay the exact config (incl. seed) for a failed clip. */
  onRetry?: (clipId: string) => void;
  /** Open clip details modal (legacy — banner title now navigates to the
   *  dedicated /avatars/:id/clips/:clipId page). */
  onShowDetails?: (clipId: string) => void;
}

type EngineFilter = 'all' | Video2dProviderId;
type BgFilter = 'all' | VideoClipTransparency;
type StatusFilter = 'all' | 'active' | 'ready' | 'inFlight' | 'archived' | 'failed';

const ENGINE_OPTIONS: { id: EngineFilter; label: string }[] = [
  { id: 'all',            label: 'All engines' },
  { id: 'pixverse-fal',   label: 'PixVerse' },
  { id: 'hailuo-fal',     label: 'Hailuo' },
  { id: 'vidu-fal',       label: 'Vidu Q3' },
  { id: 'kling-fal',      label: 'Kling 2.1' },
  { id: 'veo31-fast-fal', label: 'Veo Fast' },
  { id: 'veo31-fal',      label: 'Veo Std' },
];

const BG_OPTIONS: { id: BgFilter; label: string }[] = [
  { id: 'all',         label: 'All bg' },
  { id: 'none',        label: 'Original' },
  { id: 'chroma-key',  label: 'Transparent' },
  { id: 'matting',     label: 'True alpha' },
];

const STATUS_OPTIONS: { id: StatusFilter; label: string }[] = [
  { id: 'all',      label: 'All' },
  { id: 'active',   label: 'Active' },
  { id: 'ready',    label: 'Ready' },
  { id: 'inFlight', label: 'In flight' },
  { id: 'archived', label: 'Archived' },
  { id: 'failed',   label: 'Failed' },
];

const PROVIDER_LABEL: Record<string, string> = {
  'hailuo-fal':     'Hailuo',
  'kling-fal':      'Kling 2.1',
  'pixverse-fal':   'PixVerse',
  'vidu-fal':       'Vidu Q3',
  'veo31-fal':      'Veo 3.1',
  'veo31-fast-fal': 'Veo 3.1 Fast',
};

/**
 * Inline vertical list of all video clips for an avatar. Replaces both the
 * carousel-only selector and the bottom-sheet manage UI: every clip is a
 * banner-style row with its own inline actions (Set active, Archive,
 * Delete, Info, Reroll). Filters live at the top and stay visible while
 * the user scrolls. Long-press any row to enter bulk select mode.
 */
export function VideoClipsList({ avatar, onAdd, onReroll, onRetry, onShowDetails }: Props) {
  const haptics = useHaptics();
  const [updateClip] = useUpdateVideoClipMutation();
  const [deleteClip] = useDeleteVideoClipMutation();
  const [setActive] = useSetActiveVideoClipMutation();

  const [engine, setEngine] = useState<EngineFilter>('all');
  const [bg, setBg] = useState<BgFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');

  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pressTimer, setPressTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const clips = useMemo(() => avatar.videoClips ?? [], [avatar.videoClips]);
  const activeId = avatar.activeVideoClipId ?? null;

  // Apply chip filters first, then sort by canonical order: Active → Ready
  // → In flight → Archived → Failed; within each bucket newest first.
  const filtered = useMemo(() => {
    const matchesEngine = (c: AvatarVideoClip) => engine === 'all' || c.provider === engine;
    const matchesBg = (c: AvatarVideoClip) => bg === 'all' || (c.transparency ?? 'none') === bg;
    const matchesStatus = (c: AvatarVideoClip) => {
      if (status === 'all') return true;
      if (status === 'active')   return c.clipId === activeId && !c.archived;
      if (status === 'archived') return !!c.archived;
      if (status === 'failed')   return c.status === 'failed' && !c.archived;
      if (status === 'inFlight') return (c.status === 'queued' || c.status === 'generating') && !c.archived;
      if (status === 'ready')    return c.status === 'ready' && c.clipId !== activeId && !c.archived;
      return true;
    };
    return clips.filter(c => matchesEngine(c) && matchesBg(c) && matchesStatus(c));
  }, [clips, engine, bg, status, activeId]);

  const ordered = useMemo(() => {
    const bucket = (c: AvatarVideoClip): number => {
      if (c.archived) return 3;
      if (c.status === 'failed') return 4;
      if (c.clipId === activeId) return 0;
      if (c.status === 'ready') return 1;
      return 2; // queued + generating
    };
    return [...filtered].sort((a, b) => {
      const ba = bucket(a), bb = bucket(b);
      if (ba !== bb) return ba - bb;
      const ta = new Date(a.startedAt ?? 0).getTime();
      const tb = new Date(b.startedAt ?? 0).getTime();
      return tb - ta;
    });
  }, [filtered, activeId]);

  const archivedCount = clips.filter(c => c.archived).length;
  const visibleCount = clips.filter(c => !c.archived).length;

  /* ── Bulk handlers ────────────────────────────────────────────── */

  const enterBulk = (clipId: string) => {
    setBulkMode(true);
    setSelected(new Set([clipId]));
    haptics.impact('medium');
  };
  const toggleSel = (clipId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(clipId)) next.delete(clipId); else next.add(clipId);
      return next;
    });
    haptics.selection();
  };
  const exitBulk = () => { setBulkMode(false); setSelected(new Set()); };

  const startPress = (clipId: string) => {
    if (bulkMode) return;
    const t = setTimeout(() => enterBulk(clipId), 450);
    setPressTimer(t);
  };
  const cancelPress = () => {
    if (pressTimer) clearTimeout(pressTimer);
    setPressTimer(null);
  };

  const bulkArchive = async () => {
    for (const id of selected) {
      try { await updateClip({ avatarId: avatar._id, clipId: id, archived: true }).unwrap(); } catch { /* best-effort: skip clips that fail */ }
    }
    haptics.notification('success');
    exitBulk();
  };
  const bulkDelete = async () => {
    if (!confirm(`Delete ${selected.size} clip${selected.size === 1 ? '' : 's'} permanently?`)) return;
    for (const id of selected) {
      try { await deleteClip({ avatarId: avatar._id, clipId: id }).unwrap(); } catch { /* best-effort: skip clips that fail */ }
    }
    haptics.notification('warning');
    exitBulk();
  };

  if (!avatar.imageUrl) return null;

  return (
    <section className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between px-4">
        <h2 className="text-xs font-semibold uppercase text-fg-muted">2D Animations</h2>
        <span className="text-[11px] text-fg-muted">
          {visibleCount} active{archivedCount > 0 && ` · ${archivedCount} archived`}
        </span>
      </div>

      {/* Filter chips — sticky-ish at top of section */}
      <div className="flex flex-col gap-1.5 px-4">
        <ChipRow value={status} options={STATUS_OPTIONS} onPick={setStatus} />
        <ChipRow value={engine} options={ENGINE_OPTIONS} onPick={setEngine} />
        <ChipRow value={bg}     options={BG_OPTIONS}     onPick={setBg} />
      </div>

      {/* Primary CTA — generate a new clip. The "Static" toggle is handled
          implicitly: tap the active clip's banner once to deselect (sets
          activeVideoClipId=null), or use the Archive action. */}
      <div className="px-4">
        <button
          type="button"
          onClick={() => { haptics.impact('light'); onAdd(); }}
          className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-brand py-2.5 text-sm font-semibold text-brand-fg transition-opacity active:opacity-80"
        >
          <Plus aria-hidden className="h-4 w-4" />
          New animation
        </button>
      </div>

      {/* Vertical list of banners */}
      <div className="flex flex-col gap-2 px-4 pb-2">
        {ordered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
            <Sparkles aria-hidden className="h-6 w-6 opacity-50" />
            <p>
              {clips.length === 0
                ? "No clips yet. Tap 'New clip' to animate this avatar."
                : 'No clips match these filters.'}
            </p>
          </div>
        ) : (
          ordered.map(c => (
            <ClipBanner
              key={c.clipId}
              avatarId={avatar._id}
              avatarImageUrl={avatar.imageUrl}
              clip={c}
              isActive={c.clipId === activeId}
              bulkMode={bulkMode}
              isSelected={selected.has(c.clipId)}
              onTap={async () => {
                // Bulk mode: toggle selection. Otherwise: tap whole row to
                // set active (or deselect if it's already active).
                if (bulkMode) { toggleSel(c.clipId); return; }
                if (c.status !== 'ready' || c.archived) return;
                try {
                  const nextId: string | null = c.clipId === activeId ? null : c.clipId;
                  await setActive({ avatarId: avatar._id, clipId: nextId }).unwrap();
                  haptics.notification('success');
                } catch { haptics.notification('error'); }
              }}
              onPressStart={() => startPress(c.clipId)}
              onPressEnd={cancelPress}
              onShowDetails={onShowDetails}
              onArchive={async () => {
                try {
                  await updateClip({ avatarId: avatar._id, clipId: c.clipId, archived: !c.archived }).unwrap();
                  haptics.impact('light');
                } catch { haptics.notification('error'); }
              }}
              onDelete={async () => {
                if (!confirm('Delete this clip permanently? File will be removed.')) return;
                try {
                  await deleteClip({ avatarId: avatar._id, clipId: c.clipId }).unwrap();
                  haptics.notification('warning');
                } catch { haptics.notification('error'); }
              }}
              onReroll={onReroll}
              onRetry={onRetry}
            />
          ))
        )}
      </div>

      {/* Bulk toolbar (only when in bulk mode) */}
      {bulkMode && (
        <div className="sticky bottom-0 z-10 mx-4 mb-2 flex items-center justify-between gap-2 rounded-xl border border-border bg-bg/95 px-3 py-2 backdrop-blur shadow-lg">
          <button
            type="button"
            onClick={exitBulk}
            className="rounded-lg bg-bg-secondary px-3 py-1.5 text-xs font-semibold text-fg-muted"
          >
            Cancel
          </button>
          <p className="text-xs text-fg-muted">{selected.size} selected</p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={bulkArchive}
              disabled={selected.size === 0}
              className={cn(
                'flex items-center gap-1 rounded-lg bg-brand/10 px-2.5 py-1.5 text-xs font-semibold text-brand transition-opacity active:opacity-80',
                selected.size === 0 && 'opacity-40',
              )}
            >
              <Archive aria-hidden className="h-3.5 w-3.5" /> Archive
            </button>
            <button
              type="button"
              onClick={bulkDelete}
              disabled={selected.size === 0}
              className={cn(
                'flex items-center gap-1 rounded-lg bg-red-500/15 px-2.5 py-1.5 text-xs font-semibold text-red-400 transition-opacity active:opacity-80',
                selected.size === 0 && 'opacity-40',
              )}
            >
              <Trash2 aria-hidden className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */

interface ChipRowProps<T extends string> {
  value: T;
  options: { id: T; label: string }[];
  onPick: (v: T) => void;
}
function ChipRow<T extends string>({ value, options, onPick }: ChipRowProps<T>) {
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
      {options.map(o => (
        <button
          key={o.id}
          type="button"
          onClick={() => onPick(o.id)}
          className={cn(
            'shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold transition-colors',
            value === o.id
              ? 'border-brand bg-brand/10 text-fg'
              : 'border-border bg-bg-secondary text-fg-muted',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

interface BannerProps {
  avatarId: string;
  avatarImageUrl: string;
  clip: AvatarVideoClip;
  isActive: boolean;
  bulkMode: boolean;
  isSelected: boolean;
  onTap?: () => void;
  onPressStart: () => void;
  onPressEnd: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onShowDetails?: (clipId: string) => void;
  onReroll?: (clipId: string) => void;
  onRetry?: (clipId: string) => void;
}
function ClipBanner({
  avatarId, avatarImageUrl, clip, isActive, bulkMode, isSelected,
  onTap, onPressStart, onPressEnd,
  onArchive, onDelete, onShowDetails, onReroll, onRetry,
}: BannerProps) {
  const meta = [
    PROVIDER_LABEL[clip.provider] ?? clip.provider,
    clip.durationSec ? `${clip.durationSec}s` : null,
    clip.resolution,
    clip.transparency && clip.transparency !== 'none' ? clip.transparency : null,
  ].filter(Boolean).join(' · ');

  const statusBadge =
    clip.archived ? { label: 'archived', cls: 'bg-bg-tertiary text-fg-muted' } :
    clip.status === 'failed' ? { label: 'failed', cls: 'bg-red-500/20 text-red-400' } :
    clip.status === 'queued' ? { label: 'queued', cls: 'bg-amber-500/15 text-amber-400' } :
    clip.status === 'generating' ? { label: 'generating', cls: 'bg-amber-500/15 text-amber-400' } :
    isActive ? { label: 'active', cls: 'bg-brand/15 text-brand' } :
    null;

  return (
    <div
      onPointerDown={onPressStart}
      onPointerUp={onPressEnd}
      onPointerLeave={onPressEnd}
      onPointerCancel={onPressEnd}
      onClick={onTap}
      className={cn(
        'flex items-center gap-3 rounded-xl border p-2 transition-colors',
        isActive ? 'border-brand bg-brand/5' : 'border-border bg-bg-secondary',
        clip.archived && 'opacity-70',
      )}
    >
      {bulkMode && (
        <span
          aria-hidden
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
            isSelected ? 'border-brand bg-brand text-brand-fg' : 'border-fg-muted/40',
          )}
        >
          {isSelected && <Check aria-hidden className="h-3 w-3" />}
        </span>
      )}

      {/* Thumbnail */}
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-black/40 bg-black">
        {clip.status === 'ready' && clip.transparency === 'matting' && clip.matteStatus === 'pending' ? (
          // True-alpha matting still in flight — poster would show the
          // magenta backdrop. Show a spinner instead.
          <Loader2 aria-hidden className="m-auto mt-4 h-6 w-6 animate-spin text-brand" />
        ) : clip.status === 'ready' && clip.posterUrl ? (
          <img src={clip.posterUrl} alt="" className="h-full w-full object-cover" />
        ) : clip.status === 'ready' ? (
          <img src={avatarImageUrl} alt="" className="h-full w-full object-cover" />
        ) : clip.status === 'failed' ? (
          <Sparkles aria-hidden className="m-auto mt-4 h-6 w-6 text-red-400/60" />
        ) : (
          <Loader2 aria-hidden className="m-auto mt-4 h-6 w-6 animate-spin text-brand" />
        )}
      </div>

      {/* Title + meta. Tapping the title navigates to the dedicated clip
          page (full preview, prompt, options, seed, run metrics). The
          stopPropagation prevents the parent row's "set active" tap. */}
      <Link
        to={`/avatars/${avatarId}/clips/${clip.clipId}`}
        onClick={(e) => e.stopPropagation()}
        className="min-w-0 flex-1 transition-opacity active:opacity-80"
      >
        <div className="flex items-center gap-1.5">
          <p className="line-clamp-1 text-sm font-semibold text-fg">{clip.label ?? 'Clip'}</p>
          {statusBadge && (
            <span className={cn('rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase', statusBadge.cls)}>
              {statusBadge.label}
            </span>
          )}
        </div>
        <p className="line-clamp-1 text-[11px] text-fg-muted">{meta}</p>
      </Link>

      {/* Actions */}
      {!bulkMode && (
        <div className="flex shrink-0 items-center gap-1">
          {clip.status === 'failed' && onRetry && (
            <ActionBtn label="Retry" onClick={() => onRetry(clip.clipId)} variant="brand">
              <RefreshCw aria-hidden className="h-3.5 w-3.5" />
            </ActionBtn>
          )}
          {clip.status === 'ready' && !clip.archived && onReroll && (
            <ActionBtn label="Re-roll" onClick={() => onReroll(clip.clipId)} variant="muted">
              <Dices aria-hidden className="h-3.5 w-3.5" />
            </ActionBtn>
          )}
          {onShowDetails && (
            <ActionBtn label="Details" onClick={() => onShowDetails(clip.clipId)} variant="muted">
              <Info aria-hidden className="h-3.5 w-3.5" />
            </ActionBtn>
          )}
          <ActionBtn label={clip.archived ? 'Unarchive' : 'Archive'} onClick={onArchive} variant="muted">
            {clip.archived
              ? <ArchiveRestore aria-hidden className="h-3.5 w-3.5" />
              : <Archive aria-hidden className="h-3.5 w-3.5" />}
          </ActionBtn>
          <ActionBtn label="Delete" onClick={onDelete} variant="danger">
            <Trash2 aria-hidden className="h-3.5 w-3.5" />
          </ActionBtn>
        </div>
      )}
    </div>
  );
}

function ActionBtn({
  label, onClick, variant, children,
}: {
  label: string;
  onClick: () => void;
  variant: 'brand' | 'muted' | 'danger';
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        'rounded-lg p-1.5 transition-opacity active:opacity-80',
        variant === 'brand'  && 'bg-brand/10 text-brand',
        variant === 'muted'  && 'bg-bg-tertiary text-fg-muted',
        variant === 'danger' && 'bg-red-500/10 text-red-400',
      )}
    >
      {children}
    </button>
  );
}
